/* eslint-disable react-hooks/rules-of-hooks */

import React, { Dispatch, RefAttributes, memo, useMemo, useState } from "react";
import { Column, react } from "uikit";
import { noop } from "lodash";

import { Executor, Language } from "../../services";
import useObjectEditor from "../../hooks/useObjectEditor";
import filterExecutorsBySearch from "../../utils/executorSearch.util";
import SelectTabExtraColumn from "../../types/SelectTabExtraColumn";
import {
	useExecutorsSubHandler,
	useTaxiServiceIdsDecoder,
	useCarParkFilterAccessControl,
} from "../../hooks";

import Root from "./components/Root";
import InternalController from "./Controller";
import ModelTable from "./components/ModelTable";

const ExecutorSelectTab = memo(
	react.withController<
		ExecutorSelectTab.PropsBase,
		ExecutorSelectTab.Controller
	>(
		({
			value,
			disabled,
			disabledList,
			visible,
			language,
			allowedTaxiServiceIds,
			onChange,
			isOnFilterToCarPark = false,
			isUserCreated = false,
			isShowMagnifyingGlass = true,
			extraColumn,
			hideSelection = false,
			searchByName = false,
		}) => {
			const { subCarParkFilterAccess } =
				useCarParkFilterAccessControl<Executor.Model>();
			const { getAllExecutorsSubHandler } = useExecutorsSubHandler();
			const valueEditor = useObjectEditor(value, onChange);

			const [filters, setFilters] = useState<ModelTable.Props["filters"]>(
				{
					companyIds: ["all"],
					taxiServiceIds: ["all"],
					search: "",
				},
			);

			const decodeTaxiServiceIds = useTaxiServiceIdsDecoder({
				allowedTaxiServiceIds,
			});

			const [sort, setSort] = useState<ModelTable.Sort>({});

			const items = useMemo(() => {
				const options: Executor.SubscribeOptions = {
					taxiServiceIds: decodeTaxiServiceIds(
						filters.companyIds,
						filters.taxiServiceIds,
					),
					// Обработчик подписки ищет только по позывному; при
					// `searchByName` фильтруем сами — по позывному и ФИО.
					query: searchByName ? undefined : filters.search,
				};

				if (sort.column && sort.type)
					options.order = {
						[sort.column as keyof Executor.SubscribeOptions["order"]]:
							sort.type,
					};

				const list = getAllExecutorsSubHandler(options);

				return searchByName
					? filterExecutorsBySearch(list, filters.search)
					: list;
			}, [
				searchByName,
				decodeTaxiServiceIds,
				filters.companyIds,
				filters.search,
				filters.taxiServiceIds,
				getAllExecutorsSubHandler,
				sort.column,
				sort.type,
			]);

			const tableOnChangeSelected = valueEditor.useSetter("executorIds");

			const modelsWithFilterCarPark = useMemo(() => {
				const dataAfterAccess = isOnFilterToCarPark
					? subCarParkFilterAccess({
							models: items,
							branchPath: "cars",
							branchPath2: "park.id",
							isUserCreated,
							branchPath3: isUserCreated
								? "responsibleDispatcher.id"
								: undefined,
					  })
					: items;

				return dataAfterAccess;
			}, [
				isOnFilterToCarPark,
				isUserCreated,
				subCarParkFilterAccess,
				items,
			]);

			return (
				<Root hasPaddings={false} visible={visible}>
					<Column sizes="1fr" maxedWidth maxedHeight>
						<ModelTable
							filters={filters}
							selected={valueEditor.get("executorIds")}
							sort={sort}
							disabled={disabled}
							disabledList={disabledList}
							loading={false}
							data={modelsWithFilterCarPark}
							language={language}
							allowedTaxiServiceIds={allowedTaxiServiceIds}
							onChangeFilters={setFilters}
							onChangeSelected={tableOnChangeSelected}
							onChangeSort={setSort}
							onLoadMore={noop}
							isShowMagnifyingGlass={isShowMagnifyingGlass}
							extraColumn={extraColumn}
							hideSelection={hideSelection}
						/>
					</Column>
				</Root>
			);
		},
		InternalController,
	),
);

declare namespace ExecutorSelectTab {
	type Ref = InternalController | null;

	type Controller = InternalController;

	interface Value {
		executorIds: number[];
	}

	interface PropsBase {
		value: Value;

		disabled: boolean;
		/** A list of executors, that should be disable. */
		disabledList?: number[];

		visible: boolean;
		language: Language;

		allowedTaxiServiceIds: number[];

		onChange: Dispatch<Value>;
		isOnFilterToCarPark?: boolean;
		isUserCreated?: boolean;
		isShowMagnifyingGlass?: boolean;
		/** Доп. колонка справа (не входит в настройки колонок). */
		extraColumn?: SelectTabExtraColumn<Executor.Model>;
		/** Без колонки с галками (режим просмотра). */
		hideSelection?: boolean;
		/** Поиск по позывному и по ФИО (по умолчанию — только позывной). */
		searchByName?: boolean;
	}

	type Props = PropsBase & RefAttributes<Ref>;
}

export default ExecutorSelectTab;
